// 🎵 RadioPlayer Multi-Modes - Système de lecteur audio global
// Gère 3 modes : Annonces, Playlist, Radio avec lecture séquentielle
// Composants exportés : RadioProvider, useRadio, MiniRadioPlayer, MobileRadioPanel
// Utilisé dans : src/App.jsx (Provider) + Navigation components

import { useState, useRef, useEffect, createContext, useContext } from 'react'
import { Volume2, Volume1, VolumeX, Radio, Music, Megaphone, SkipBack, SkipForward, Play, Pause } from 'lucide-react'
import { VOCALS, MUSICS, RADIO_CONFIG, PLAYER_MODES, AUTO_SEQUENCE } from '@/data/audioConfig'

/**
 * Context React pour le lecteur audio global
 * Permet de partager l'état du lecteur dans toute l'application
 */
const RadioContext = createContext()

/**
 * Hook custom pour accéder au context radio
 * @returns {Object} État et fonctions du lecteur
 */
export function useRadio() {
  return useContext(RadioContext)
}

/**
 * Provider du lecteur audio - Gère 3 modes de lecture
 * - Annonce : Fichiers vocals en séquence
 * - Playlist : Fichiers musics avec navigation
 * - Radio : Stream RTL2 Littoral
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Composants enfants
 */
export function RadioProvider({ children }) {
  // États principaux
  const [mode, setMode] = useState(PLAYER_MODES.ANNONCE)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(70)
  
  // Index de lecture
  const [currentAnnonceIndex, setCurrentAnnonceIndex] = useState(0)
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(0)
  
  // Séquence automatique
  const [autoSequenceEnabled, setAutoSequenceEnabled] = useState(AUTO_SEQUENCE.enabled)
  const [sequenceCompleted, setSequenceCompleted] = useState(false)
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false)
  
  const audioRef = useRef(null)

  // Obtenir la source audio actuelle selon le mode
  const getCurrentSource = () => {
    if (mode === PLAYER_MODES.ANNONCE && VOCALS.length > 0) {
      return VOCALS[currentAnnonceIndex]?.file || null
    } else if (mode === PLAYER_MODES.PLAYLIST && MUSICS.length > 0) {
      return MUSICS[currentPlaylistIndex]?.file || null
    } else if (mode === PLAYER_MODES.RADIO) {
      return RADIO_CONFIG.stream
    }
    return null
  }

  // Obtenir les infos du track actuel
  const getCurrentTrack = () => {
    if (mode === PLAYER_MODES.ANNONCE && VOCALS.length > 0) {
      return {
        ...VOCALS[currentAnnonceIndex],
        index: currentAnnonceIndex,
        total: VOCALS.length
      }
    } else if (mode === PLAYER_MODES.PLAYLIST && MUSICS.length > 0) {
      return {
        ...MUSICS[currentPlaylistIndex],
        index: currentPlaylistIndex,
        total: MUSICS.length
      }
    } else if (mode === PLAYER_MODES.RADIO) {
      return {
        title: RADIO_CONFIG.title,
        artist: RADIO_CONFIG.artist,
        isLive: true
      }
    }
    return null
  }

  /**
   * Changement de mode manuel (désactive la séquence auto)
   */
  const switchMode = (newMode) => {
    setMode(newMode)
    setAutoSequenceEnabled(false) // Désactive séquence auto lors du switch manuel
    setIsPlaying(false) // Stop avant de changer
    
    // Optionnel : redémarrer automatiquement dans le nouveau mode
    setTimeout(() => setIsPlaying(true), 100)
  }

  /**
   * Passer au track suivant (playlist uniquement)
   */
  const nextTrack = () => {
    if (mode !== PLAYER_MODES.PLAYLIST) return
    
    if (currentPlaylistIndex < MUSICS.length - 1) {
      setCurrentPlaylistIndex(prev => prev + 1)
    } else {
      // Fin de la playlist
      setCurrentPlaylistIndex(0) // Retour au début
    }
  }

  /**
   * Passer au track précédent (playlist uniquement)
   */
  const previousTrack = () => {
    if (mode !== PLAYER_MODES.PLAYLIST) return
    
    if (currentPlaylistIndex > 0) {
      setCurrentPlaylistIndex(prev => prev - 1)
    } else {
      // Début de la playlist
      setCurrentPlaylistIndex(MUSICS.length - 1) // Va à la fin
    }
  }

  /**
   * Gestion de la fin d'un track (séquence automatique)
   */
  const handleTrackEnded = () => {
    // Ne rien faire si la séquence auto est désactivée
    if (!autoSequenceEnabled) {
      setIsPlaying(false)
      return
    }

    if (mode === PLAYER_MODES.ANNONCE) {
      // Annonce suivante ou passage à la playlist
      if (currentAnnonceIndex < VOCALS.length - 1) {
        setCurrentAnnonceIndex(prev => prev + 1)
      } else {
        // Fin des annonces
        if (MUSICS.length > 0) {
          setMode(PLAYER_MODES.PLAYLIST)
          setCurrentPlaylistIndex(0)
        } else {
          // Pas de playlist, direct radio
          setMode(PLAYER_MODES.RADIO)
        }
      }
    } else if (mode === PLAYER_MODES.PLAYLIST) {
      // Morceau suivant ou passage à la radio
      if (currentPlaylistIndex < MUSICS.length - 1) {
        setCurrentPlaylistIndex(prev => prev + 1)
      } else {
        // Fin de la playlist
        setMode(PLAYER_MODES.RADIO)
        setSequenceCompleted(true)
      }
    }
    // Mode radio : pas de fin (stream continu)
  }

  // Mettre à jour la source audio quand le mode/index change
  useEffect(() => {
    if (!audioRef.current) return
    
    const source = getCurrentSource()
    if (source) {
      audioRef.current.src = source
      
      // Reprendre la lecture si on était en train de jouer
      if (isPlaying) {
        audioRef.current.play().catch(err => {
          console.error('Erreur lecture:', err)
          setIsPlaying(false)
        })
      }
    }
  }, [mode, currentAnnonceIndex, currentPlaylistIndex])

  // Contrôle play/pause
  useEffect(() => {
    if (!audioRef.current) return
    
    if (isPlaying) {
      audioRef.current.play().catch(err => {
        console.error('Erreur lecture audio:', err)
        setIsPlaying(false)
      })
    } else {
      audioRef.current.pause()
    }
  }, [isPlaying])

  // Contrôle volume
  useEffect(() => {
    if (!audioRef.current) return
    audioRef.current.volume = volume / 100
  }, [volume])

  // Listener pour la fin de track
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.addEventListener('ended', handleTrackEnded)
    return () => audio.removeEventListener('ended', handleTrackEnded)
  }, [mode, currentAnnonceIndex, currentPlaylistIndex, autoSequenceEnabled])

  // Auto-play au démarrage (après 2s)
  useEffect(() => {
    if (hasAutoPlayed) return
    
    const timer = setTimeout(() => {
      setIsPlaying(true)
      setHasAutoPlayed(true)
    }, 2000)

    return () => clearTimeout(timer)
  }, [hasAutoPlayed])

  const value = {
    // Mode
    mode,
    setMode,
    switchMode,
    
    // Lecture
    isPlaying,
    setIsPlaying,
    volume,
    setVolume,
    
    // Navigation
    nextTrack,
    previousTrack,
    
    // Données
    currentTrack: getCurrentTrack(),
    currentAnnonceIndex,
    currentPlaylistIndex,
    vocals: VOCALS,
    musics: MUSICS,
    radioConfig: RADIO_CONFIG,
    
    // Séquence
    autoSequenceEnabled,
    setAutoSequenceEnabled,
    sequenceCompleted,
    
    // Ref
    audioRef
  }

  return (
    <RadioContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="metadata" />
    </RadioContext.Provider>
  )
}

/**
 * Onglets de sélection de mode
 */
function ModeTabs({ className = "" }) {
  const { mode, switchMode, vocals, musics } = useRadio()

  const tabs = [
    {
      id: PLAYER_MODES.ANNONCE,
      label: 'Annonce',
      icon: Megaphone,
      count: vocals.length,
      disabled: vocals.length === 0
    },
    {
      id: PLAYER_MODES.PLAYLIST,
      label: 'Playlist',
      icon: Music,
      count: musics.length,
      disabled: musics.length === 0
    },
    {
      id: PLAYER_MODES.RADIO,
      label: 'Radio',
      icon: Radio,
      disabled: true  // TEMPORAIRE: En attente accord diffusion RTL2
    }
  ]

  return (
    <div className={`flex gap-0 bg-neutral-800 ${className}`}>
      {tabs.map((tab) => {
        const Icon = tab.icon
        const isActive = mode === tab.id
        
        return (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && switchMode(tab.id)}
            disabled={tab.disabled}
            title={tab.label}
            className={`
              flex flex-col items-center justify-center gap-0.5 py-2 px-3 text-xs font-medium transition-all flex-1 border-b-2
              ${isActive 
                ? 'bg-neutral-900 text-primary-500 border-primary-500' 
                : 'text-neutral-400 hover:text-neutral-200 border-transparent'
              }
              ${tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <Icon size={16} />
            {tab.count !== undefined && (
              <span className="text-[9px] opacity-70">{tab.count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Affichage du track en cours
 */
function TrackDisplay({ className = "" }) {
  const { currentTrack, mode } = useRadio()

  if (!currentTrack) return null

  return (
    <div className={`${className}`}>
      <p className="font-medium text-neutral-900 dark:text-neutral-100">
        {currentTrack.title || 'Sans titre'}
      </p>
      {currentTrack.artist && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          {currentTrack.artist}
        </p>
      )}
      {(mode === PLAYER_MODES.ANNONCE || mode === PLAYER_MODES.PLAYLIST) && currentTrack.total && (
        <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
          {currentTrack.index + 1} / {currentTrack.total}
        </p>
      )}
      {currentTrack.isLive && (
        <div className="flex items-center gap-2 mt-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-500">En direct</span>
        </div>
      )}
    </div>
  )
}


/**
 * Mini lecteur audio compact (version desktop avec hover)
 * @param {Object} props
 * @param {string} props.className - Classes CSS additionnelles
 * @param {boolean} props.showControls - État externe pour afficher les contrôles (optionnel)
 * @param {boolean} props.isDesktop - Mode desktop (pour compatibilité)
 */
export function MiniRadioPlayer({ className = "", showControls: externalShowControls, isDesktop = false }) {
  const { isPlaying, setIsPlaying, volume, setVolume, mode, audioRef, currentTrack, previousTrack, nextTrack } = useRadio()
  const [isOpen, setIsOpen] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)

  // Mettre à jour la progression
  useEffect(() => {
    const audio = audioRef?.current
    if (!audio) return

    const updateTime = () => {
      setCurrentTime(audio.currentTime || 0)
      setDuration(audio.duration || 0)
    }

    audio.addEventListener('timeupdate', updateTime)
    audio.addEventListener('loadedmetadata', updateTime)

    return () => {
      audio.removeEventListener('timeupdate', updateTime)
      audio.removeEventListener('loadedmetadata', updateTime)
    }
  }, [audioRef])

  // Formater le temps en mm:ss
  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Progression en pourcentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className={`relative ${className}`}>
      {/* Bouton Ultra-Compact */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-3 bg-gradient-to-br from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all group"
        title={`${isPlaying ? 'En lecture' : 'Lecture'} - ${currentTrack?.title || mode}`}
      >
        {/* Icône selon le mode */}
        {mode === 'radio' ? (
          <Radio size={20} />
        ) : mode === 'playlist' ? (
          <Music size={20} />
        ) : (
          <Megaphone size={20} />
        )}
        
        {/* Badge de lecture */}
        {isPlaying && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
        )}
        
        {/* Badge progression (si track avec durée) */}
        {mode !== 'radio' && duration > 0 && (
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 56 56">
            <circle
              cx="28"
              cy="28"
              r="26"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray={`${progress * 1.63} 163`}
              className="text-white/30"
            />
          </svg>
        )}
      </button>

      {/* Panel de contrôles détaillé (toggle au clic) */}
      {isOpen && (
        <>
          {/* Backdrop pour fermer au clic extérieur */}
          <div 
            className="fixed inset-0 z-[90]" 
            onClick={() => setIsOpen(false)}
          />
          
          <div className="absolute top-full right-0 mt-2 bg-neutral-900 rounded-xl shadow-2xl w-72 overflow-hidden z-[100]">
            {/* Onglets EN HAUT */}
            <ModeTabs className="" />
            
            <div className="p-4">
              {/* Info Track */}
              <div className="text-center mb-4">
                <p className="text-sm font-semibold text-white truncate">
                  {currentTrack?.title || 'Aucun titre'}
                </p>
                <p className="text-xs text-neutral-400 truncate">
                  {currentTrack?.artist || mode}
                </p>
              </div>

              {/* Barre de progression + temps */}
              {mode !== 'radio' && duration > 0 && (
                <div className="mb-4">
                  <div className="relative h-1 bg-neutral-700 rounded-full overflow-hidden cursor-pointer group">
                    <div 
                      className="absolute inset-y-0 left-0 bg-white rounded-full"
                      style={{ width: `${progress}%` }}
                    />
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100"
                      style={{ left: `${progress}%`, marginLeft: '-6px' }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              )}

              {/* Contrôles */}
              <div className="flex items-center justify-center gap-4 mb-4">
                {mode === PLAYER_MODES.PLAYLIST && (
                  <button onClick={previousTrack} className="text-neutral-400 hover:text-white transition-colors">
                    <SkipBack size={20} />
                  </button>
                )}
                
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="p-2 bg-white hover:scale-105 rounded-full transition-transform"
                >
                  {isPlaying ? (
                    <Pause size={20} fill="black" className="text-black" />
                  ) : (
                    <Play size={20} fill="black" className="text-black ml-0.5" />
                  )}
                </button>
                
                {mode === PLAYER_MODES.PLAYLIST && (
                  <button onClick={nextTrack} className="text-neutral-400 hover:text-white transition-colors">
                    <SkipForward size={20} />
                  </button>
                )}
              </div>
            
              {/* Volume simple */}
              <div className="flex items-center gap-2">
                <button onClick={() => setVolume(volume > 0 ? 0 : 70)} className="text-neutral-400 hover:text-white transition-colors">
                  {volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  className="flex-1 h-1 bg-neutral-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
                />
                <span className="text-xs text-neutral-400 w-8 text-right">{volume}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Panel radio mobile fullscreen
 */
export function MobileRadioPanel({ isOpen, onClose }) {
  const { isPlaying, setIsPlaying, volume, setVolume } = useRadio()

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-white dark:bg-neutral-900 flex flex-col animate-in slide-in-from-bottom duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
        <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Lecteur Audio</h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg"
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-6 overflow-y-auto">
        {/* Mode Tabs */}
        <ModeTabs className="mb-6" />
        
        {/* Track Display */}
        <div className="flex-1 flex items-center justify-center">
          <TrackDisplay className="text-center" />
        </div>
        
        {/* Playlist Controls */}
        <PlaylistControls className="justify-center mb-6" />
        
        {/* Play/Pause */}
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="w-20 h-20 mx-auto bg-gradient-to-br from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-full shadow-lg flex items-center justify-center mb-6"
        >
          {isPlaying ? <Pause size={32} fill="currentColor" /> : <Play size={32} fill="currentColor" />}
        </button>
        
        {/* Volume */}
        <div className="flex items-center gap-4 px-4">
          <button
            onClick={() => setVolume(volume > 0 ? 0 : 70)}
            className="text-neutral-600 dark:text-neutral-400"
          >
            {volume === 0 ? <VolumeX size={24} /> : <Volume2 size={24} />}
          </button>
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            className="flex-1 h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg"
          />
          <span className="text-sm text-neutral-600 dark:text-neutral-400 min-w-[3ch]">
            {volume}%
          </span>
        </div>
      </div>
    </div>
  )
}
